import type { RouteDefinitions } from "@/utils/$route-client";
import {
  type FetchRouteOptions,
  type HttpMethod,
  type RouteDefinitionMap,
  type RouteMethod,
  type RoutePathnameFor,
  type RouteResponseJson,
  buildRouteUrl,
  createFetchRoute,
} from "@/utils/route-client";
import useSWR, { type SWRConfiguration, type SWRResponse } from "swr";
import useSWRMutation, {
  type SWRMutationConfiguration,
  type SWRMutationResponse,
} from "swr/mutation";

/**
 * Route Handlerがokでないレスポンスを返した場合のエラー
 *
 * @knipignore
 */
export class RouteHandlerError extends Error {
  constructor(
    readonly status: number,
    // レスポンスのボディ（JSONとして解釈できればパースしたもの、できなければ文字列）
    readonly body: unknown,
  ) {
    super(`Route Handler responded with status ${status}`);
    this.name = "RouteHandlerError";
  }
}

const readBody = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// application/json と application/*+json をJSONとみなす
const isJsonResponse = (res: Response): boolean => {
  const mimeType = (res.headers.get("Content-Type") ?? "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  return mimeType === "application/json" || mimeType.endsWith("+json");
};

// okならJSON（JSON以外はBlob）を返し、そうでなければRouteHandlerErrorをthrowする
const parseResponse = async (res: Response): Promise<unknown> => {
  if (!res.ok) {
    throw new RouteHandlerError(res.status, await readBody(res));
  }
  return isJsonResponse(res) ? res.json() : res.blob();
};

// SWRで扱うレスポンスの型（JSONを返さないrouteはBlob）
type RouteResponseData<
  Defs extends RouteDefinitionMap,
  P extends keyof Defs,
  M extends HttpMethod,
> = [RouteResponseJson<Defs, P, M>] extends [never]
  ? Blob
  : RouteResponseJson<Defs, P, M>;

type MutationMethod = Exclude<HttpMethod, "GET" | "HEAD">;

type RouteMutationMethod<
  Defs extends RouteDefinitionMap,
  P extends keyof Defs,
> = Extract<RouteMethod<Defs, P>, MutationMethod>;

// 更新系のメソッドを持つrouteのパス
type RoutePathnameWithMutation<Defs extends RouteDefinitionMap> = {
  [P in keyof Defs & string]: [RouteMutationMethod<Defs, P>] extends [never]
    ? never
    : P;
}[keyof Defs & string];

// 全てのオプションが省略可能な場合は trigger の引数を省略できるようにする
type MutationArg<O> = object extends O ? O | undefined : O;

// 返却値の型が分かるGETのハンドラーを持つrouteのパス
// （JSONの型が分かるもの、またはJSONを返さないと宣言されているもの。型が分からない unknown を除く）
type TypedGetRoutePathname<Defs extends RouteDefinitionMap> = {
  [P in RoutePathnameFor<Defs, "GET">]: unknown extends RouteResponseJson<
    Defs,
    P,
    "GET"
  >
    ? never
    : P;
}[RoutePathnameFor<Defs, "GET">];

type GetOptions<
  Defs extends RouteDefinitionMap,
  P extends keyof Defs & string,
> = Omit<FetchRouteOptions<Defs, "GET", P>, "body">;

// 全てのオプションが省略可能な場合は引数自体を省略可能にする
// options に null を渡すとリクエストしない（条件付きの取得）
type UseRouteHandlerArgs<O, Data> = object extends O
  ? [options?: O | null, config?: SWRConfiguration<Data, RouteHandlerError>]
  : [options: O | null, config?: SWRConfiguration<Data, RouteHandlerError>];

/**
 * 指定したrouteの定義に対する useRouteHandler / useRouteHandlerMutation を作る
 *
 * 通常は useRouteHandler / useRouteHandlerMutation を使う（テストで任意のrouteの定義を使うためのもの）
 *
 * @knipignore
 */
export const createRouteHandlerHooks = <Defs extends RouteDefinitionMap>() => {
  const useRouteHandler = <P extends TypedGetRoutePathname<Defs>>(
    pathname: P,
    ...[options, config]: UseRouteHandlerArgs<
      GetOptions<Defs, P>,
      RouteResponseData<Defs, P, "GET">
    >
  ): SWRResponse<RouteResponseData<Defs, P, "GET">, RouteHandlerError> => {
    const { params, query, init } = (options ?? {}) as {
      params?: Record<string, string | number>;
      query?: Parameters<typeof buildRouteUrl>[1]["query"];
      init?: RequestInit;
    };
    const url = buildRouteUrl(pathname, { params, query });

    return useSWR<RouteResponseData<Defs, P, "GET">, RouteHandlerError>(
      options === null ? null : ["route-handler", "GET", url],
      async () =>
        parseResponse(await fetch(url, { ...init, method: "GET" })) as Promise<
          RouteResponseData<Defs, P, "GET">
        >,
      config,
    );
  };

  // 型の絞り込みは useRouteHandlerMutation の引数で行うので、ここでは緩い型で呼び出す
  const fetchRoute = createFetchRoute<Defs>() as unknown as (
    pathname: string,
    method: MutationMethod,
    options?: unknown,
  ) => Promise<Response>;

  const useRouteHandlerMutation = <
    P extends RoutePathnameWithMutation<Defs>,
    M extends RouteMutationMethod<Defs, P>,
  >(
    pathname: P,
    method: M,
    config?: SWRMutationConfiguration<
      RouteResponseData<Defs, P, M>,
      RouteHandlerError,
      readonly [string, P, M],
      MutationArg<FetchRouteOptions<Defs, M, P>>
    >,
  ): SWRMutationResponse<
    RouteResponseData<Defs, P, M>,
    RouteHandlerError,
    readonly [string, P, M],
    MutationArg<FetchRouteOptions<Defs, M, P>>
  > => {
    return useSWRMutation(
      ["route-handler", pathname, method] as const,
      async (
        _key,
        { arg }: { arg: MutationArg<FetchRouteOptions<Defs, M, P>> },
      ) =>
        parseResponse(await fetchRoute(pathname, method, arg)) as Promise<
          RouteResponseData<Defs, P, M>
        >,
      config,
    );
  };

  return { useRouteHandler, useRouteHandlerMutation };
};

const hooks = createRouteHandlerHooks<RouteDefinitions>();

/**
 * Route Handler（GET）からSWRでJSON・ファイルを取得する
 *
 * - 返却値の型が分かるGETのハンドラーを持つrouteのみ指定できる
 *   （route.ts のハンドラーの戻り値を RouteHandlerResponse<Json>、JSON以外なら RouteHandlerResponse で宣言する）
 * - レスポンスの Content-Type がJSON（application/json, application/*+json）ならJSON、それ以外は Blob を返す
 * - パスパラメータ・クエリ・レスポンスのJSONの型は fetchRoute と同じく route.ts の定義から決まる
 * - レスポンスがokでない場合は error に RouteHandlerError が入る
 * - options に null を渡すとリクエストしない
 *
 * @example
 * const { data, error, isLoading } = useRouteHandler("/employers/[officeId]/items", {
 *   params: { officeId },
 *   query: { page: 1 },
 * });
 */
export const useRouteHandler = hooks.useRouteHandler;

/**
 * Route Handler（POST / PUT / PATCH / DELETE）をSWRのmutationとして呼び出す
 *
 * - 更新系のハンドラーを持つrouteを先に指定し、メソッドはそのrouteでexportされているものだけ指定できる
 * - レスポンスは useRouteHandler と同じく、JSONならJSON、それ以外は Blob を返す
 * - trigger の引数にパスパラメータ・クエリ・ボディを渡す（型は fetchRoute と同じく route.ts の定義から決まる）
 * - レスポンスがokでない場合は trigger が RouteHandlerError をthrowし、error にも入る
 *
 * @example
 * const { trigger, isMutating } = useRouteHandlerMutation("/employers/[officeId]/items", "POST");
 * const created = await trigger({ params: { officeId }, body: { name } });
 *
 * @knipignore
 */
export const useRouteHandlerMutation = hooks.useRouteHandlerMutation;
